import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Alert } from '@material-ui/lab';
import { showAlert } from 'actions/app';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { UserProviderContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import { Permissions, userHasPermission, userHasRoles } from 'services/Authorization';
import {
  createSearchProject,
  SEARCH_PARAMS_KEYS,
  SEARCH_PROJECT_CREATED_FROM_VARIANTS,
  updateSearchProject
} from 'services/searchProjects';
import { getAllSearchProjectsFolders } from 'services/searchProjects/folders';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPRadioGroup from 'UI/components/molecules/FPRadioGroup';
import InfoLabel from 'UI/components/molecules/InfoLabel';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import TreeViewSelector from 'UI/components/molecules/TreeViewSelector/TreeViewSelector';
import { buildCloseAndSaveButtonDefinition } from 'UI/components/templates/FPDrawer';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import {
  DEFAULT_SEARCH_PROJECT_FOLDERS_EXCLUDE_IDS,
  SEARCH_PROJECT_ROOT_FOLDERS,
  SEARCH_PROJECT_SOURCES,
  SearchProjectPrivacyScopes
} from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { SUPER_USER_ROLES } from 'UI/constants/roles';
import { hasFeatureFlag, idOptionSelected } from 'UI/utils';
import { prepareFormToSubmit } from 'UI/utils/forms';

import {
  CONTACT_TYPE_PAYLOAD_MAP,
  IMPORT_TARGET_PAYLOAD_MAP,
  SKIP_DUPLICATES_PAYLOAD_MAP
} from './components/ImportOptionsForm/ImportOptionsForm.constants';
import { ImportOptionsForm, IndustryDetailsForm } from './components';
import { FORM_FIELDS_MAP } from './fields';
import SearchProjectExtractOptions from './SearchProjectExtractOptions';
import SearchProjectMasterListFields from './SearchProjectMasterListFields';
import { useExtractableCompaniesCount } from './useExtractableCompaniesCount';
import { getExtractionAlertConfig, hasFiltersApplied } from './utils';

/** @typedef {import('types/app').SearchProjectFormDrawerPropTypes} SearchProjectFormDrawerPropTypes */
const { searchProject: formCopies } = strings.searchProjects.forms;

const PRIVACY_RADIO_OPTIONS = [
  SearchProjectPrivacyScopes.private,
  SearchProjectPrivacyScopes.public
];

const getFinalTitle = ({ isSearchProjectBeingEdited, shouldExtractCompanyContacts }) => {
  if (shouldExtractCompanyContacts) return formCopies.titles.extractCompaniesContacts;

  return isSearchProjectBeingEdited ? formCopies.titles.edit : formCopies.titles.create;
};

const getSearchProjectBaseInitialValues = ({
  searchProject,
  isSearchProjectBeingEdited,
  shouldExtractCompanyContacts
}) => {
  const isSearchProjectSet = searchProject && typeof searchProject === 'object';
  const defaultPrivacyScope = SearchProjectPrivacyScopes.private.id;
  if (isSearchProjectSet) {
    if (isSearchProjectBeingEdited)
      return {
        name: searchProject.name,
        privacyScope: SearchProjectPrivacyScopes.getPrivacyState(searchProject.isPrivate).id
      };
    if (shouldExtractCompanyContacts)
      return {
        name: `${searchProject.name} - ${formCopies.sections.extractCompaniesContacts.nameSufix}`,
        privacyScope: defaultPrivacyScope
      };
  }

  return { name: null, privacyScope: defaultPrivacyScope };
};

/**
 * @param {SearchProjectFormDrawerPropTypes} props
 */
const SearchProjectFormDrawer = ({
  bulkId = null,
  createdFrom = null,
  extraPayloadData = null,
  isSearchProjectBeingEdited = false,
  itemsToAdd = null,
  onClose,
  onSubmitSearchProject = null,
  queryParams = null,
  searchProject = null,
  shouldExtractCompanyContacts = false,
  shouldMoveCopiedItems = false
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [currentUser] = useContext(UserProviderContext);

  const isOwnSearchProject = searchProject?.owner?.id === currentUser?.id;
  const isCurrentUserSuperUser = userHasRoles(SUPER_USER_ROLES);
  const shouldShowOwnerSelector =
    hasFeatureFlag(FeatureFlags.SearchProjectsChangeOwner) &&
    isCurrentUserSuperUser &&
    isSearchProjectBeingEdited;
  const canMarkAsMasterList = userHasPermission(Permissions.SearchProjects.CanMarkAsMasterList);
  const shouldShowMasterListFields =
    canMarkAsMasterList && hasFeatureFlag(FeatureFlags.SearchProjectsMasterListFields);
  const shouldShowFoldersSelector =
    (!isSearchProjectBeingEdited || isOwnSearchProject) &&
    hasFeatureFlag(FeatureFlags.SearchProjectsFolders);
  const finalTitle = getFinalTitle({ isSearchProjectBeingEdited, shouldExtractCompanyContacts });
  const isFromProspectingAssistant =
    extraPayloadData?.sourceId === SEARCH_PROJECT_SOURCES.coresignal.id;
  const isProspectingCompany =
    isFromProspectingAssistant && extraPayloadData?.entityType === EntityType.Company;

  const areFiltersApplied = hasFiltersApplied(queryParams);
  const {
    count: extractionCompaniesCount,
    hasError: hasExtractableCompaniesCountError,
    isLoading: isLoadingCompaniesToExtractCount
  } = useExtractableCompaniesCount({
    queryParams,
    searchProjectId: searchProject?.id,
    shouldExtractCompanyContacts
  });
  const hasCompaniesToExtract = extractionCompaniesCount > 0;
  const shouldDisableExtractionSubmit =
    shouldExtractCompanyContacts &&
    (isLoadingCompaniesToExtractCount ||
      hasExtractableCompaniesCountError ||
      !hasCompaniesToExtract);

  const extractionAlertConfig = getExtractionAlertConfig({
    extractionCompaniesCount,
    hasCompaniesToExtract,
    hasExtractableCompaniesCountError,
    hasFiltersAppliedForExtraction: areFiltersApplied,
    isLoadingCompaniesToExtractCount,
    shouldExtractCompanyContacts
  });

  const formDefaultValues = useMemo(() => {
    const defaultValues = {};
    const baseInitialValues = getSearchProjectBaseInitialValues({
      searchProject,
      isSearchProjectBeingEdited,
      shouldExtractCompanyContacts
    });
    defaultValues[FORM_FIELDS_MAP.name.key] = baseInitialValues.name;
    defaultValues[FORM_FIELDS_MAP.privacyScope.key] = baseInitialValues.privacyScope;

    if (shouldShowOwnerSelector) {
      defaultValues[FORM_FIELDS_MAP.owner.key] = searchProject.owner;
    }

    if (shouldShowMasterListFields) {
      if (isSearchProjectBeingEdited) {
        defaultValues[FORM_FIELDS_MAP.isMasterList.key] = searchProject.isMasterList;
        defaultValues[FORM_FIELDS_MAP.geoPolygon.key] = searchProject.geoPolygon
          ? searchProject.geoPolygon.polygon
          : null;
        defaultValues[FORM_FIELDS_MAP.seatCount.key] = searchProject.seatCount;
      } else {
        defaultValues[FORM_FIELDS_MAP.isMasterList.key] = false;
        defaultValues[FORM_FIELDS_MAP.geoPolygon.key] = null;
        defaultValues[FORM_FIELDS_MAP.seatCount.key] = null;
      }
    }

    if (shouldShowFoldersSelector) {
      defaultValues[FORM_FIELDS_MAP.parentFolder.key] = isSearchProjectBeingEdited
        ? searchProject?.folder
        : SEARCH_PROJECT_ROOT_FOLDERS.mine;
    }

    return defaultValues;
  }, [
    isSearchProjectBeingEdited,
    searchProject,
    shouldExtractCompanyContacts,
    shouldShowFoldersSelector,
    shouldShowMasterListFields,
    shouldShowOwnerSelector
  ]);

  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false
  });

  const form = useForm({
    defaultValues: formDefaultValues,
    mode: 'onChange'
  });
  const { errors, handleSubmit, register, setValue, unregister, watch } = form;
  const scopeWatcher = watch(FORM_FIELDS_MAP.privacyScope.key);
  const ownerWatcher = watch(FORM_FIELDS_MAP.owner.key);

  useEffect(() => {
    register({ name: FORM_FIELDS_MAP.privacyScope.key });
  }, [register]);

  useEffect(() => {
    if (shouldShowOwnerSelector) {
      register(
        { name: FORM_FIELDS_MAP.owner.key },
        {
          validate: {
            required: value => value?.id || formCopies.sections.owner.requiredMessage,
            sameCollaboratorAsOwner: value => {
              const someCollaboratorSelectedAsOwner = searchProject?.collaborators.some(
                ({ user_id }) => user_id === value?.id
              );

              return someCollaboratorSelectedAsOwner
                ? formCopies.sections.owner.sameCollaboratorAsOwnerMessage
                : true;
            }
          }
        }
      );
    }

    return () => {
      unregister(FORM_FIELDS_MAP.owner.key);
    };
  }, [searchProject, register, shouldShowOwnerSelector, unregister]);

  useEffect(() => {
    if (shouldShowFoldersSelector)
      register(
        { name: FORM_FIELDS_MAP.parentFolder.key },
        { required: formCopies.sections.parentFolder.requiredMessage }
      );
  }, [shouldShowFoldersSelector, register]);

  const handleRadioChange = event => setValue(FORM_FIELDS_MAP.privacyScope.key, event.target.value);

  const searchParamsKey =
    createdFrom === SEARCH_PROJECT_CREATED_FROM_VARIANTS.bulkMetrics
      ? SEARCH_PARAMS_KEYS.MetricsParams
      : SEARCH_PARAMS_KEYS.SearchParams;

  const onSubmit = handleSubmit(async formData => {
    setUiState({ isSaving: true, isSuccess: false });

    const optionalParams = {};
    if (searchProject?.id) optionalParams.searchProjectId = searchProject?.id;
    if (shouldShowMasterListFields) {
      optionalParams.canMarkAsMasterList = canMarkAsMasterList;
      if (isSearchProjectBeingEdited) optionalParams.geoPolygonId = searchProject.geoPolygon?.id;
    }

    if (shouldShowOwnerSelector) {
      optionalParams.shouldSetNewOwnerId =
        searchProject.owner.id !== formData[FORM_FIELDS_MAP.owner.key].id;
    }

    if (isFromProspectingAssistant) {
      if (!isProspectingCompany) {
        optionalParams[FORM_FIELDS_MAP.prospectType.key] =
          CONTACT_TYPE_PAYLOAD_MAP[formData[FORM_FIELDS_MAP.prospectType.key]];
      }
      optionalParams[FORM_FIELDS_MAP.skipDuplicates.key] =
        SKIP_DUPLICATES_PAYLOAD_MAP[formData[FORM_FIELDS_MAP.skipDuplicates.key]];

      const importTargetKey = isProspectingCompany
        ? extraPayloadData.entityType
        : formData[FORM_FIELDS_MAP.prospectType.key];

      optionalParams.importTarget = IMPORT_TARGET_PAYLOAD_MAP[importTargetKey];
    }
    const { alert, data, success } = await (isSearchProjectBeingEdited
      ? updateSearchProject({
          ...formData,
          ...optionalParams
        })
      : createSearchProject({
          ...omitBy(prepareFormToSubmit(formData, FORM_FIELDS_MAP), isNil),
          ...optionalParams,
          bulkId,
          extraPayloadData,
          itemsToAdd,
          queryParams,
          searchParamsKey,
          shouldExtractCompanyContacts,
          shouldMoveCopiedItems
        }));

    dispatch(showAlert(alert));

    if (success) {
      onSubmitSearchProject && onSubmitSearchProject();
      !isSearchProjectBeingEdited &&
        setTimeout(() => {
          history.push(EntityRoutes.SearchProjectsPreview.replace(':id', data.id), {
            createdFrom: createdFrom || 'default'
          });
        }, 1);
    }

    setUiState({ isSaving: false, isSuccess: success });
  });

  const handleSelectNewOwner = (name, user) => {
    const finalUser = user
      ? {
          fullName: user?.full_name,
          id: user?.id
        }
      : null;
    setValue(name, finalUser, true);
  };

  const buttonDefinitions = buildCloseAndSaveButtonDefinition({
    onClose,
    isSaveDisabled: shouldDisableExtractionSubmit,
    isSaving: uiState.isSaving
  });

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      CardHeaderProps={{ title: finalTitle }}
      onClose={onClose}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit, id: 'drawer-form' }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <FormContext {...form}>
        <When condition={!!extractionAlertConfig}>
          <Alert severity={extractionAlertConfig?.severity}>
            <Typography variant="body2">
              {extractionAlertConfig?.message}
              {!!extractionAlertConfig?.companiesCount && (
                <b>
                  {` ${extractionAlertConfig.companiesCount} ${extractionAlertConfig.companiesLabel}`}
                </b>
              )}
            </Typography>
          </Alert>
        </When>
        <NumberedForm>
          <When condition={shouldExtractCompanyContacts}>
            <NumberedForm.Item title={formCopies.sections.titles.extractionDetails}>
              <Grid container>
                <Grid item xs={6}>
                  <InfoLabel
                    title={formCopies.sections.extractCompaniesContacts.details.extractingFrom}
                    variant="body1"
                    description={searchProject?.name}
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoLabel
                    title={formCopies.sections.extractCompaniesContacts.details.companiesToExtract}
                    variant="body1"
                    description={
                      isLoadingCompaniesToExtractCount || hasExtractableCompaniesCountError
                        ? '-'
                        : extractionCompaniesCount
                    }
                  />
                </Grid>
              </Grid>
            </NumberedForm.Item>
          </When>
          <NumberedForm.Item title={formCopies.sections.titles.details}>
            <TextBox
              label={formCopies.sections.name.label}
              name={FORM_FIELDS_MAP.name.key}
              inputRef={register({
                required: formCopies.sections.name.requiredMessage,
                minLength: {
                  value: 5,
                  message: formCopies.sections.name.minLengthMessage
                }
              })}
              error={!!errors[FORM_FIELDS_MAP.name.key]}
              errorText={errors[FORM_FIELDS_MAP.name.key]?.message}
            />
            <FPRadioGroup
              options={PRIVACY_RADIO_OPTIONS}
              onChange={handleRadioChange}
              value={scopeWatcher}
              adviceText={
                formCopies.sections.visibility[SearchProjectPrivacyScopes[scopeWatcher].id]
              }
            />
            <When condition={shouldShowOwnerSelector}>
              <AutocompleteSelect
                error={!!errors[FORM_FIELDS_MAP.owner.key]}
                errorText={errors[FORM_FIELDS_MAP.owner.key]?.message}
                getOptionLabel={user => user.fullName || user.full_name || ''}
                getOptionSelected={idOptionSelected}
                name={FORM_FIELDS_MAP.owner.key}
                onSelect={handleSelectNewOwner}
                placeholder={formCopies.sections.owner.placeholder}
                renderOption={({ full_name }) => full_name}
                selectedValue={ownerWatcher}
                typeahead
                typeaheadLimit={15}
                url={Endpoints.Users}
              />
            </When>
          </NumberedForm.Item>
          <When condition={shouldExtractCompanyContacts}>
            <NumberedForm.Item title={formCopies.sections.titles.extractCompaniesContacts}>
              <SearchProjectExtractOptions />
            </NumberedForm.Item>
          </When>
          <When condition={shouldShowFoldersSelector}>
            <NumberedForm.Item title={formCopies.sections.titles.parentFolder}>
              <TreeViewSelector
                formFieldName={FORM_FIELDS_MAP.parentFolder.key}
                placeholder={formCopies.sections.parentFolder.placeholder}
                TreeViewLoaderProps={{
                  enableIconClick: true,
                  onlyShowFolders: true,
                  service: {
                    method: getAllSearchProjectsFolders,
                    queryParams: { excludeIds: DEFAULT_SEARCH_PROJECT_FOLDERS_EXCLUDE_IDS }
                  }
                }}
              />
            </NumberedForm.Item>
          </When>
          <When condition={shouldShowMasterListFields && !shouldExtractCompanyContacts}>
            <SearchProjectMasterListFields />
          </When>

          <When condition={isFromProspectingAssistant && !isSearchProjectBeingEdited}>
            <IndustryDetailsForm isCompany={isProspectingCompany} />
            <ImportOptionsForm isCompany={isProspectingCompany} />
          </When>
        </NumberedForm>
      </FormContext>
    </FPDrawer>
  );
};

export default SearchProjectFormDrawer;
